import { certificatesPublicApi } from '@/lib/api/student-certificates-public'

/**
 * ⚠️ `issuedAt`, `completedAt` e `revokedAt` são `Type.String()` na API e
 * `string` no tipo gerado — e mesmo assim chegam como `Date`.
 *
 * O Eden Treaty passa um reviver ao `JSON.parse` que converte toda string com
 * cara de data (inclusive `YYYY-MM-DD`) em `Date`. A conversão acontece DEPOIS
 * da fronteira, do lado do cliente: não há bug no servidor para nenhum gate
 * achar, e `data.slice(0, 10)` derruba a tela inteira em runtime. Tipar `string`
 * aqui seria mentira — por isso `string | Date` até o formatador.
 */
type ApiDate = string | Date

export type PublicCertificateView = {
  code: string
  studentName: string
  programTitle: string
  totalLessons: number
  issuedAt: ApiDate
  completedAt: ApiDate | null
}

/**
 * Os estados que a página sabe desenhar.
 *
 * 🔴 `unavailable` existe separado de `not_found` de propósito. Um 429, um
 * 400 de workspace não resolvido ou a API fora do ar NÃO podem ser desenhados
 * como "certificado não encontrado": quem confere um diploma leria uma falha de
 * infraestrutura como prova de fraude, e é o candidato que paga a conta.
 */
export type VerificationResult =
  | { state: 'valid'; certificate: PublicCertificateView }
  | { state: 'revoked'; certificate: PublicCertificateView | null; revokedAt: ApiDate | null }
  | { state: 'not_found' }
  | { state: 'invalid_code' }
  | { state: 'unavailable'; rateLimitExceeded: boolean; detail: string }

/**
 * Consulta a rota pública e traduz a resposta para os estados da tela.
 *
 * Nunca lança: a página de verificação não tem para onde escalar um erro — um
 * `errorComponent` genérico diria ao empregador "algo deu errado" sem dizer se o
 * diploma vale. Todo caminho vira um estado desenhável.
 */
export async function verifyCertificate(code: string): Promise<VerificationResult> {
  try {
    const { data, error } = await certificatesPublicApi().api.certificates.verificar({ code }).get()

    if (error) {
      return {
        state: 'unavailable',
        rateLimitExceeded: error.status === 429,
        detail: `HTTP ${error.status}`,
      }
    }

    /*
     * Resposta achatada (a API evita `Type.Union` porque o `normalize` do Elysia
     * resolve união por tentativa e some com campo da variante errada).
     *
     * ✅ 20/09/2026 — as CHAVES da resposta viraram inglês nos dois lados no mesmo
     * commit (`api/src/schemas/core/certificate.schema.ts`). Restam em português
     * os VALORES de `reason` (`revogado`, `codigo_invalido`, `nao_encontrado`),
     * casados por igualdade logo abaixo: outra mudança de dois lados.
     *
     * Esta função segue sendo a FRONTEIRA: dela para dentro, só inglês.
     */
    if (data.valid && data.certificate) {
      return { state: 'valid', certificate: data.certificate }
    }

    if (data.reason === 'revogado') {
      return {
        state: 'revoked',
        certificate: data.certificate ?? null,
        revokedAt: data.revokedAt ?? null,
      }
    }

    if (data.reason === 'codigo_invalido') return { state: 'invalid_code' }

    return { state: 'not_found' }
  } catch (err) {
    // Rede caída, timeout do fetcher (10s), DNS. O `catch` fecha o buraco que o
    // `error` do treaty não cobre: ali só chega resposta HTTP.
    return {
      state: 'unavailable',
      rateLimitExceeded: false,
      detail: err instanceof Error ? err.message : 'falha de rede',
    }
  }
}
